import React, { useEffect, useState } from "react";
import { fetchAll, createDoc, updateDocById, deleteDocById, orderBy, WithId } from "../lib/firestoreData";

interface Duyuru {
  baslik: string;
  tarih: string;
  sira: number;
  aciklama?: string;
}
type Row = Duyuru & WithId;

const COLLECTION = "duyurular";
const bos = { baslik: "", tarih: "", sira: "", aciklama: "" };

const DuyurularPage: React.FC = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [hata, setHata] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<{ baslik: string; tarih: string; sira: string; aciklama: string }>(bos);
  const [formError, setFormError] = useState("");

  const load = async () => {
    setLoading(true);
    setHata(false);
    try {
      setRows(await fetchAll<Duyuru>(COLLECTION, orderBy("sira", "asc")));
    } catch (e) {
      console.error("Duyurular okunamadı:", e);
      setHata(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(bos); setEditId(""); setFormError(""); };
  const openEdit = (r: Row) => {
    setForm({ baslik: r.baslik, tarih: r.tarih, sira: String(r.sira ?? ""), aciklama: r.aciklama ?? "" });
    setEditId(r.id);
    setFormError("");
  };
  const close = () => { setEditId(null); setFormError(""); };

  const save = async () => {
    setFormError("");
    if (!form.baslik.trim() || !form.tarih.trim() || form.sira.trim() === "") {
      setFormError("Başlık, tarih ve sıra zorunludur.");
      return;
    }
    const siraNum = parseInt(form.sira, 10);
    if (Number.isNaN(siraNum)) { setFormError("Sıra geçerli bir sayı olmalıdır."); return; }
    setSaving(true);
    try {
      const payload: Duyuru = { baslik: form.baslik.trim(), tarih: form.tarih.trim(), sira: siraNum, aciklama: form.aciklama.trim() };
      if (editId) await updateDocById<Duyuru>(COLLECTION, editId, payload);
      else await createDoc<Duyuru>(COLLECTION, payload);
      close();
      await load();
    } catch (e) {
      console.error("Kayıt başarısız:", e);
      setFormError("Kayıt başarısız, lütfen tekrar deneyin.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r: Row) => {
    if (!window.confirm(`"${r.baslik}" silinsin mi?`)) return;
    try { await deleteDocById(COLLECTION, r.id); await load(); }
    catch (e) { console.error("Silme başarısız:", e); window.alert("Silme başarısız."); }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Duyurular</h2>
          <p className="text-sm text-gray-500 mt-0.5">{rows.length} duyuru · mobil Ana Sayfa "Duyurular"</p>
        </div>
        <button onClick={openNew} className="bg-[#8B1428] hover:bg-[#6f0f20] text-white text-sm font-semibold px-4 py-2.5 rounded-lg flex items-center gap-2 transition-colors">
          <i className="fa-solid fa-plus" /> Yeni Duyuru
        </button>
      </div>

      {loading ? (
        <div className="py-20 text-center text-gray-400"><i className="fa-solid fa-circle-notch fa-spin text-3xl" /></div>
      ) : hata ? (
        <div className="bg-red-50 text-red-600 rounded-2xl py-12 text-center text-sm">
          Duyurular yüklenemedi. <button onClick={load} className="underline font-semibold">Tekrar dene</button>
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-300 py-20 text-center">
          <i className="fa-solid fa-bullhorn text-5xl text-gray-300 mb-4" />
          <p className="text-gray-500 font-medium">Henüz duyuru eklenmemiş</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase w-16">Sıra</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Başlık</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Tarih</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map(r => (
                <tr key={r.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-gray-400 font-mono align-top">{r.sira}</td>
                  <td className="px-4 py-3 align-top">
                    <div className="font-medium text-gray-800">{r.baslik}</div>
                    {r.aciklama ? (
                      <div className="text-xs text-gray-400 mt-0.5 max-w-md truncate">{r.aciklama}</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs align-top">{r.tarih}</td>
                  <td className="px-4 py-3 align-top">
                    <div className="flex items-center gap-3">
                      <button onClick={() => openEdit(r)} className="text-blue-600 hover:text-blue-800 text-xs font-semibold">Düzenle</button>
                      <button onClick={() => remove(r)} className="text-gray-400 hover:text-red-500"><i className="fa-solid fa-trash text-xs" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editId !== null && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={close} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 relative">
              <button onClick={close} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700"><i className="fa-solid fa-xmark text-lg" /></button>
              <h3 className="text-lg font-bold text-gray-800 mb-5">{editId ? "Duyuruyu Düzenle" : "Yeni Duyuru"}</h3>
              <div className="space-y-4">
                <FieldD label="Başlık *"><input className={inputClsD} value={form.baslik} onChange={e => setForm({ ...form, baslik: e.target.value })} /></FieldD>
                <FieldD label="Tarih *"><input className={inputClsD} value={form.tarih} onChange={e => setForm({ ...form, tarih: e.target.value })} placeholder="01.06.2026" /></FieldD>
                <FieldD label="Sıra *"><input type="number" className={inputClsD} value={form.sira} onChange={e => setForm({ ...form, sira: e.target.value })} placeholder="1" /></FieldD>
                <FieldD label="Açıklama"><textarea className={inputClsD} rows={4} value={form.aciklama} onChange={e => setForm({ ...form, aciklama: e.target.value })} placeholder="Duyuru detayı (opsiyonel)" /></FieldD>
                {formError && <div className="text-red-600 text-sm">{formError}</div>}
                <div className="flex gap-3 pt-2">
                  <button onClick={close} className="flex-1 border border-gray-200 text-gray-600 text-sm font-semibold py-2.5 rounded-lg hover:bg-gray-50">İptal</button>
                  <button onClick={save} disabled={saving} className="flex-1 bg-[#8B1428] hover:bg-[#6f0f20] text-white text-sm font-semibold py-2.5 rounded-lg disabled:opacity-60">
                    {saving ? "Kaydediliyor..." : "Kaydet"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const inputClsD = "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B1428]/30 text-gray-900";
const FieldD: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
    {children}
  </div>
);

export default DuyurularPage;
